<template>

    <div class="route-project-form">
        <div class="padding-box">

            <cms-heading class="page-heading" level="1" :title="pageTitle" icon="magicon-folder-opened">
                <cms-button color="positive" @click="submit" :loading="submitting">Opslaan</cms-button>
                <cms-button color="medium-dark" type="bordered" :to="{ name: 'projects' }">Annuleren</cms-button>
                <cms-button color="negative" type="bordered" v-if="projectId" @click="deleteProject" style="margin-right: 10px;">Project verwijderen</cms-button>
            </cms-heading>

            <div class="wm-block" v-spinner="loading">

                <cms-form v-if="!loading">
                    <cms-form-input title="Titel" name="title" data-vv-name="title" data-vv-as="Titel"
                                    v-model="editProject.title" v-validate="'required'" :error="error('title')"></cms-form-input>

                    <cms-form-input title="Code" name="code" data-vv-name="code" data-vv-as="Code"
                                    v-model="editProject.code" v-validate="'required'" :error="error('code')"></cms-form-input>

                    <cms-form-input title="Organisatie" name="organisation" data-vv-name="organisation" data-vv-as="Organisatie"
                                    v-model="editProject.organisation" v-validate="'required'" :error="error('organisation')"></cms-form-input>

                    <cms-form-input title="Naam contactpersoon" name="contactperson" data-vv-name="contactperson" data-vv-as="Contactpersoon"
                                    v-model="editProject.contactPersonName" v-validate="'required'" :error="error('contactperson')"></cms-form-input>

                    <cms-form-radio title="Status" name="state" data-vv-name="state" data-vv-as="Status"
                                    v-model="editProject.state" :options="[{label: 'Actief', value: 'OPEN'}, {label: 'Inactief', value: 'CLOSED'}]" v-validate="'required'" :error="error('state')"></cms-form-radio>

                    <cms-form-date-picker title="Start datum" name="startDate" data-vv-name="startDate" data-vv-as="Start datum"
                                    v-model="editProject.startDate" v-validate="'required'" :error="error('startDate')"></cms-form-date-picker>

                    <cms-form-place-searchbar title="Locatie" name="location" :value="editProject.locationTitle" @update="setPlace"></cms-form-place-searchbar>

                    <div class="divider"></div>

                    <cms-form-row class="linked-users" title="Gekoppelde gebruikers" v-if="users">
                        <div class="top">
                            <span class="hint">Vink de gebruikers aan die gekoppeld moeten zijn aan dit project</span>
                            <cms-searchbar v-model="usersSearchQuery"></cms-searchbar>
                        </div>

                        <div v-spinner="loadingUsers">
                            <table class="table clickable table-linked-users">
                                <cms-table-header>
                                    <cms-th style="width: 5%;"><cms-checkbox :value="allVisibleUsersSelected" @input="toggleAllVisibleUsers"></cms-checkbox></cms-th>
                                    <cms-th field="firstName" v-model="usersSort">Voornaam</cms-th>
                                    <cms-th field="lastName" v-model="usersSort">Achternaam</cms-th>
                                    <cms-th field="role" v-model="usersSort">Rol</cms-th>
                                    <cms-th field="email" v-model="usersSort">E-mail adres</cms-th>
                                </cms-table-header>
                                <tr v-for="user of users.items" :class="{ selected: checkedUserIds.indexOf(user.id) >= 0 }" @click="onUserSelect(user.id)">
                                    <td @click.stop>
                                        <cms-checkbox :value="checkedUserIds.indexOf(user.id) >= 0" @input="onUserSelect(user.id)"></cms-checkbox>
                                    </td>
                                    <td>{{ user.firstName }}</td>
                                    <td>{{ user.lastName }}</td>
                                    <td>{{ user.role | userRole }}</td>
                                    <td>{{ user.email }}</td>
                                </tr>
                            </table>
                        </div>

                    </cms-form-row>

                </cms-form>

            </div>

        </div>
    </div>

</template>

<script>

    import Component from 'vue-class-component'
    import Vue from 'global-vue'

    import pick from 'lodash/pick'
    import clone from 'lodash/clone'
    import without from 'lodash/without'
    import map from 'lodash/map'
    import difference from 'lodash/difference'
    import intersection from 'lodash/intersection'

    import validationMixin from '@wittignl/js-core/mixins/validation'

    import createQuery from '@wittignl/js-core/apollo/queries/createQuery'
    import FindProjectQuery from 'gql/queries/ProjectForm/FindProject.gql'
    import AllUsersQuery from 'gql/queries/ProjectForm/AllUsers.gql'

    import ProjectMutations from 'app/mutations/ProjectMutations';

    import ProjectDataSource from 'app/enums/ProjectDataSource';

    @Component({

        name: 'project-form',
        mixins: [validationMixin],
        apollo: {

            project: createQuery(FindProjectQuery, {

                fetchPolicy: 'cache-and-network',

                skip() {

                    return !this.projectId;
                },

                response(response) {

                    this.editProject = pick(response, Object.keys(this.editProject));

                    this.originalCheckedUserIds = map(response.users.items, user => user.id);
                    this.checkedUserIds = clone(this.originalCheckedUserIds);
                },

                variables() {

                    return {
                        projectId: this.projectId
                    }
                }
            }),
            users: createQuery(AllUsersQuery, {

                fetchPolicy: 'cache-and-network',
                loadingKey: 'loadingUsers',

                variables(){

                    return {
                        offset: this.offset,
                        limit: this.itemsPerPage,
                        search: this.usersSearchQuery,
                        sortField: this.usersSort.field,
                        sortDirection: this.usersSort.direction
                    }
                }
            })
        }
    })
    export default class ProjectForm extends Vue {

        static USERS_SORT_STORAGE_KEY = "ProjectFormUsersSort";
        static USERS_SEARCH_STORAGE_KEY = "ProjectFormUsersSearch";

        data() {

            return {

                loading: 0,
                loadingUsers: 0,
                submitting: false,
                submitted: false,

                project: null,
                users: null,

                checkedUserIds: [],
                originalCheckedUserIds: [],

                editProject: {
                    title: null,
                    code: null,
                    organisation: null,
                    contactPersonName: null,
                    state: null,
                    startDate: null,
                    locationTitle: null,
                    locationLat: null,
                    locationLng: null
                },

                offset: 0,
                itemsPerPage: 30,
                usersSearchQuery: this.$storage.getSession(ProjectForm.USERS_SEARCH_STORAGE_KEY, null),
                usersSort: this.$storage.getSession(ProjectForm.USERS_SORT_STORAGE_KEY, {
                    field: 'lastName',
                    direction: 'ASC'
                })
            };
        }

        created() {

            this.logger = this.$di.logger.child('ProjectForm');

            this.$updateStore('usersSort', ProjectForm.USERS_SORT_STORAGE_KEY, true);
            this.$updateStore('usersSearchQuery', ProjectForm.USERS_SEARCH_STORAGE_KEY, true);

            // Reset paging when searching
            this.$watch('usersSearchQuery', () => {
                this.offset = 0;
            });
        }

        breadcrumb() {

            return this.pageTitle;
        }

        get pageTitle(){

            return this.projectId ? 'Project wijzigen' : 'Project toevoegen';
        }

        onUserSelect(userId) {

            if(this.checkedUserIds.indexOf(userId) >= 0) {

                this.checkedUserIds = without(this.checkedUserIds, userId);
            }
            else {

                this.checkedUserIds.push(userId);
            }
        }

        setPlace(place, description) {

            const location = place && place.geometry ? place.geometry.location : null;

            this.editProject.locationLat = location ? location.lat() : null;
            this.editProject.locationLng = location ? location.lng() : null;
            this.editProject.locationTitle = location ? description : null;
        }

        async submit() {

            this.submitted = true;

            const success = await this.$validator.validateAll();
            if(!success) {

                this.$toast.warning('Niet alle velden zijn correct ingevuld');
                return;
            }

            this.submitting = true;
            const input = clone(this.editProject);

            try {

                if(this.project) {

                    const addedUsers = difference(this.checkedUserIds, this.originalCheckedUserIds);
                    const removedUsers = difference(this.originalCheckedUserIds, this.checkedUserIds);

                    // UPDATING
                    input.id = this.project.id;
                    await ProjectMutations.update(input, addedUsers, removedUsers);

                    this.logger.info('Project updated');
                    this.$toast.success('Het project is aangepast');
                }
                else {

                    // CREATING
                    await ProjectMutations.create(
                        input,
                        this.checkedUserIds
                    );

                    this.logger.info('Project created');
                    this.$toast.success('Het project is aangemaakt');
                }

                this.$router.replace({
                    name: 'projects'
                });
            }
            catch(e) {

                var firstErrorCode = null;

                if((e.graphQLErrors || []).length > 0){
                    firstErrorCode = e.graphQLErrors[0].code;
                }

                this.logger.error(`Error updating/creating project (${firstErrorCode})`, e);

                if(firstErrorCode === 101){
                    this.$toast.error('De gekozen code is al in gebruik');
                }
                else {
                    this.$toast.error('Het project kon niet worden opgeslagen');
                }
            }

            this.submitting = false;
        }

        async deleteProject(){

            const { result, modal } = await this.$di.alert.confirm('Weet je zeker dat je dit project wilt verwijderen?', true);

            if(!result){
                return;
            }

            try {

                await ProjectMutations.delete(this.project.id);

                this.logger.info(`Project ${this.project.id} deleted`);
                this.$toast.success('Het project is verwijderd');

                this.$router.replace({
                    name: 'projects'
                });
            }
            catch(e){

                this.logger.error(`Error deleting project ${this.project.id}`, e);
                this.$toast.error('Het project kon niet worden verwijderd');
            }

            this.$modal.close(modal);
        }

        get projectId() {

            return this.$route.params.id;
        }

        allVisibleUsersSelected() {

            if(!this.users) return false;

            let visibleUserIds = this.users.items.map(user => user.id);

            return intersection(visibleUserIds, this.checkedUserIds).length === visibleUserIds.length && visibleUserIds.length > 0;
        }

        toggleAllVisibleUsers(checked) {

            if(!this.users) return;

            let visibleUserIds = this.users.items.map(user => user.id);
            this.checkedUserIds = checked ? this.checkedUserIds.concat(visibleUserIds) : [];
        }
    }

</script>
